import { motion, AnimatePresence } from "framer-motion";
import { Tag as TagIcon } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { springs } from "../motion";
import { TagCard } from "./TagCard";
import type { TagListProps, Tag } from "./types";

export function TagList({
  tags,
  onEdit,
  onDelete,
  onCreate,
  editingTagId,
  deletingTagId,
  onCancelEdit,
  onSaveEdit,
  onCancelDelete,
  onConfirmDelete,
}: TagListProps) {
  const { t } = useTranslation(["common"]);
  const isManaged = editingTagId !== undefined && deletingTagId !== undefined;

  if (tags.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={springs.gentle}
        className="flex flex-col items-center justify-center py-10 text-center"
      >
        <TagIcon className="h-6 w-6 text-muted mb-2" />
        <p className="text-[13px] text-muted mb-2">
          {t("emptyStates.noTagsYet")}
        </p>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            className="text-[13px] text-accent hover:underline"
          >
            {t("emptyStates.createFirstTag")}
          </button>
        )}
      </motion.div>
    );
  }

  const getMode = (tag: Tag): "view" | "edit" | "delete" => {
    if (isManaged && editingTagId === tag.id) return "edit";
    if (isManaged && deletingTagId === tag.id) return "delete";
    return "view";
  };

  return (
    <div className="border border-border rounded-card overflow-hidden">
      <AnimatePresence mode="popLayout" initial={false}>
        {tags.map((tag, index) => (
          <TagCard
            key={tag.id}
            tag={tag}
            mode={getMode(tag)}
            onStartEdit={() => onEdit(tag)}
            onCancelEdit={() => onCancelEdit?.()}
            onSave={onSaveEdit ?? (async () => {})}
            onStartDelete={() => onDelete(tag)}
            onCancelDelete={() => onCancelDelete?.()}
            onConfirmDelete={onConfirmDelete ?? (async () => {})}
            animationDelay={index * 0.03}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}
